/**
 * Eval module: layout (header, sidebar, breadcrumb, access denied), the landing
 * page, the homepage, the placeholder pages, Usage, Insights, Tasks, Golden
 * Datasets, Candidates, Evaluators, and the Reports page with its Online and
 * Offline tabs. Keys must match ../en/eval.ts.
 * Agent names (Rocky, Acquisition Agent, Campaign Copilot) are names and are not here.
 * Stat labels from /home/stats, evaluator output, intents, channels, task types,
 * statuses and test case fields come from the server. Evaluator names, ids and
 * levels are technical identifiers.
 */
const evalNs = {
  agentTitle: 'Eval - {{agent}}',
  backToRocky: '返回 Rocky',
  denied: {
    title: '无权访问评测模块',
    account: '账号 <strong>{{uid}}</strong> 无权访问评测模块。',
    group: '所需 LDAP 组：<code>{{group}}</code>',
    hint: '请联系管理员申请权限。',
    back: '← 返回 Rocky Portal',
  },
  crumbs: {
    eval: 'Eval',
    homepage: '首页',
    usage: '使用情况',
    insights: '洞察',
    tasks: '任务',
    reports: '报告',
    datasets: '数据集',
    candidates: '候选用例',
    evaluators: '评估器',
    metrics: '指标',
    settings: '设置',
  },
  header: {
    openNav: '打开导航',
    feedback: '反馈',
    notifications: '通知',
    help: '帮助',
    settings: '设置',
  },
  sidebar: {
    expand: '展开侧边栏',
    collapse: '收起侧边栏',
    details: '详情',
    platform: '评测平台',
    homepage: '首页',
    reports: '报告',
    usage: '使用情况',
    insights: '洞察',
    tasks: '任务',
    datasets: '黄金数据集',
    evaluators: '评估器',
  },
  placeholder: {
    body: '页面开发中，内容将根据设计稿补充。',
  },
  landing: {
    title: 'Eval 评测平台',
    description:
      '一站式评测、监控和优化 AI agent 全生命周期表现的平台。通过在线和离线评测、自定义 LLM-as-judge 评估器衡量质量，跟踪使用情况和响应延迟，管理用于回归测试的黄金数据集，并给出可执行的洞察，持续提升 agent 的可靠性和用户体验。',
    descLabel: '简介',
    activeUsers: '活跃用户',
    sessions: '会话',
    noPermission: '无权限',
    agents: {
      rocky: '面向 CMHK 业务团队的内部数据分析 agent。',
      acquisition_agent: '客户获取流程自动化 agent。',
      campaign_copilot_agent: '营销活动策划与执行助手。',
      marketing_agent: '营销 agent：活动策划、内容与分析。',
    },
  },
  home: {
    filters: { today: '今天', d7: '7 天', d30: '30 天', m3: '3 个月' },
    viewAll: '查看全部',
    noEvalData: '该 agent 暂无评测数据。',
    usage: '使用情况',
    usageLink: '使用分析',
    loadingUsage: '正在加载使用数据…',
    usageFailed: '加载使用数据失败',
    noUsage: '该时间段内没有使用数据。',
    activeUsers: '活跃用户',
    sessions: '会话',
    messages: '消息',
    toolCalls: '{{n}} 次工具调用',
    tokens: 'Token',
    tokensSplit: '输入 {{input}} · 输出 {{output}}',
    latency: '平均 / P95 响应时间',
    channels: '渠道分布',
    noChannels: '暂无渠道数据。',
    online: '在线评测',
    loadingStats: '正在加载统计…',
    statsFailed: '加载统计失败',
    noStats: '该时间段内没有统计数据。',
    offline: '离线评测',
    loading: '加载中…',
    metricsFailed: '加载指标失败',
    noRuns: '该时间段内没有上线前评测。',
    avgPassRate: '平均通过率',
    acrossOne: '共 {{n}} 次运行',
    acrossMany: '共 {{n}} 次运行',
    promotionRate: '发布通过率',
    promoted: '{{n}} 次通过',
    skipped: '{{n}} 次跳过',
    blocked: '{{n}} 次拦截',
    integration: '集成测试：{{passed}}/{{total}} 通过',
    integrationRunning: '集成测试：{{passed}}/{{total}} 通过 · {{running}} 个运行中',
    errorLog: '错误日志',
    insightsLink: '查看洞察',
    loadingFailures: '正在加载失败用例…',
    failuresFailed: '加载失败用例失败',
    noFailures: '没有失败用例 🎉',
    search: '搜索',
    searchLabel: '筛选错误日志',
    prevPage: '上一页',
    nextPage: '下一页',
    colName: '名称',
    colReason: '失败原因',
    colCategory: '类别',
    noMatch: '没有匹配的用例。',
  },
  shared: {
    /** Locale for last-active / created times in the Eval tables. */
    dateLocale: 'zh-CN',
    /** Locale for the "Last update time" stamp; '' is the browser's default. */
    updatedLocale: 'zh-CN',
    lastUpdate: '最后更新时间：{{time}}',
    startDate: '开始日期',
    endDate: '结束日期',
    noData: '暂无数据',
    findResources: '搜索',
    prev: '← 上一页',
    next: '下一页 →',
    page: '第 {{page}} / {{total}} 页',
  },
  usage: {
    title: 'Eval - {{agent}} 使用情况',
    loading: '正在加载使用指标…',
    loadFailed: '加载指标失败',
    section: '使用情况',
    activeUsers: '活跃用户',
    sessions: '会话',
    messages: '消息',
    messagesSub: '{{conversations}} 条对话 · {{toolCalls}} 次工具调用',
    inputTokens: '输入 Token',
    outputTokens: '输出 Token',
    toolCalls: '工具调用',
    avgResponse: '平均响应时间',
    p95Response: 'P95 响应时间',
    latencyBreakdown: '延迟明细',
    scheduled: '定时任务：',
    interactive: '交互对话：',
    dailySessions: '每日会话',
    dayBar: '{{date}}：{{n}} 个会话',
    channels: '渠道分布',
    responseTimes: '响应时间分布（{{n}} 个样本）',
    topTools: '常用工具',
    colTool: '工具',
    colCalls: '调用次数',
    allUsers: '全部用户（{{n}}）',
    searchUsers: '搜索用户',
    colUser: '用户',
    colRole: '职位',
    colSessions: '会话',
    colTotalTokens: '总 Token',
    colInputTokens: '输入 Token',
    colOutputTokens: '输出 Token',
    colChannel: '渠道',
    colLastActive: '最近活跃',
  },
  insights: {
    title: 'Eval - {{agent}} 洞察',
    reportDate: '报告日期',
    latestReport: '最新报告',
    reportOption: '{{date}}（{{n}} 个会话）',
    loading: '正在加载洞察报告…',
    loadFailed: '加载洞察失败',
    noData: '暂无洞察数据',
    tabOverview: '意图概览',
    tabOptimization: '优化建议',
    tabFailures: '失败用例',
    intentSessions: '会话：{{n}}',
    description: '描述',
    conversation: '会话记录',
    loadingConversation: '正在加载会话…',
    conversationFailed: '加载会话失败',
    noConversation: '没有会话消息。',
    loadingCases: '正在加载失败用例…',
    casesFailed: '加载失败用例失败',
    sourceRun: '来源运行：',
    noCases: '该时间段内没有失败用例。',
    score: '得分：{{score}}',
    session: '会话：',
    trace: 'Trace：{{id}}',
    span: 'Span：{{id}}',
    rootCause: '根因',
    suggestion: '建议',
    explanation: '评估器原始说明',
    noOptimization: '暂无优化数据',
    optimizationCategory: '优化类别',
    sessionsOf: '{{n}}/{{total}} 个会话',
    affectedSessions: '受影响会话',
    rootCauses: '根因数',
    rootCauseName: '根因：{{name}}',
    recommendation: '建议：{{text}}',
  },
  tasks: {
    title: 'Eval - {{agent}} 任务',
    loading: '正在加载任务指标…',
    loadFailed: '加载任务指标失败',
    section: '使用情况',
    activeTasks: '活跃任务',
    executions: '执行次数',
    successRate: '成功率',
    failed: '失败',
    dailyExecutions: '每日执行',
    success: '成功',
    dayBar: '{{date}}：成功 {{success}} 次，失败 {{failed}} 次',
    typeDistribution: '任务类型分布',
    colType: '类型',
    colCount: '数量',
    durationDistribution: '耗时分布',
    colBucket: '区间',
    topFailed: '失败最多的任务（{{n}}）',
    allTasks: '全部任务（{{n}}）',
    noFailing: '没有失败的任务。',
    colName: '名称',
    colOwner: '负责人',
    colFailures: '失败次数',
    colLastError: '最近错误',
    colLastFailure: '最近失败时间',
    colCreator: '创建人',
    colSchedule: '执行计划',
    colLastStatus: '最近状态',
    colLastExecution: '最近执行',
    colCreated: '创建时间',
    colExecs: '执行（{{n}} 天）',
    colFails: '失败（{{n}} 天）',
  },
  datasets: {
    agentDefault: 'rocky（默认）',
    title: 'Eval - Rocky - 黄金数据集',
    subtitle: '管理离线评测用例：创建、编辑和整理黄金数据集',
    header: '黄金数据集',
    info: '说明',
    delete: '删除',
    edit: '编辑',
    reviewCandidates: '审核候选用例（{{n}}）',
    importJson: '导入 JSON',
    addCase: '新增测试用例',
    allSuites: '全部用例集',
    allStatuses: '全部状态',
    statusPlaceholder: '状态',
    loading: '正在加载测试用例…',
    noCases: '暂无测试用例',
    noMatches: '没有匹配项',
    noCasesBody: '还没有黄金测试用例，新建一个开始吧。',
    viewAll: '查看全部',
    status: { active: '启用', inactive: '停用', draft: '草稿' },
    col: {
      id: '用例 ID',
      mode: '模式',
      prompt: '输入提示词',
      suite: '用例集',
      profile: '评测身份',
      source: '来源',
      creator: '创建人',
      status: '状态',
      agent: '运行时 Agent',
    },
    multiTurn: '多轮',
    singleTurn: '单轮',
    legacyAlias: '{{alias}}（旧版）',
    source: { candidate: '评测候选', migration: '迁移', import: '导入', manual: '手动' },
    confirmDelete: '确定删除 {{n}} 个测试用例？',
    deleted: '已删除 {{n}} 个测试用例',
    deleteFailed: '删除失败：{{error}}',
    unknownError: '未知错误',
    imported: '成功导入 {{n}} 个用例',
    importFailed: '导入失败：{{error}}',
    profileRequired: '必须选择评测身份',
    updated: '测试用例已更新',
    created: '测试用例已创建',
    opFailed: '操作失败：{{error}}',
    oneTurn: '只检测到 1 轮对话，按单轮保存',
    modal: {
      editTitle: '编辑测试用例',
      createTitle: '新建测试用例',
      cancel: '取消',
      confirm: '确认',
      newSuite: '+ 新建用例集…',
      selectSuite: '选择用例集',
      metrics: '评测指标',
      metricsDesc: '可多选',
      selectMetrics: '选择指标',
      channelType: '渠道类型',
      profileDesc: '所选的评测身份会和该用例一起保存。',
      profileDisabled: '{{alias}}（已停用）',
      selectProfile: '选择评测身份',
      noProfiles: '没有可用的评测身份',
      expectedTools: '预期工具',
      commaSeparated: '用逗号分隔',
      domainTags: '领域标签',
      domainTagsDesc: '用逗号分隔。用 domain/sub_domain 标记某个子领域（如 cmhk-sales/revenue）；只写 domain 表示整个领域。',
      skillAgent: '技能所属 Agent',
      skillAgentDesc: '该用例技能标签所属的 agent',
      skillAgentNoTags: '填写技能标签后生效；现在显示的是该用例运行时的 agent',
      runAgent: '运行时 Agent',
      runAgentDefault: '默认（rocky）',
      runAgentDesc: '实际运行这条用例的 agent。不选时：有技能标签的用例用「技能所属 Agent」运行，其余用 rocky。',
      skillTags: '技能标签',
      skillTagsDesc: '技能名称，用逗号分隔（都属于上面选择的 agent）',
      onlineIgnore: '在线评测忽略',
      scheduled: '定时任务',
      integration: '集成测试',
      inputMode: '输入模式',
      inputPrompt: '输入提示词 *',
      inputPromptPlaceholder: '要评测的用户问题…',
      turns: '对话轮次 *（按最终回复评测）',
      turn: '第 {{n}} 轮',
      turnPlaceholder: '用户消息 #{{n}}',
      expectedOutput: '预期输出 - <em>选填</em>',
      placeholder: '请输入',
      context: '上下文',
      rawJson: '原始 JSON',
      structured: '结构化视图',
    },
    ctx: {
      mustBeObject: '上下文必须是 JSON 对象',
      invalidJson: 'JSON 格式错误，请切换到原始 JSON 修改',
      noFields: '还没有上下文字段，点击「+ 添加字段」开始。',
      enterValue: '请输入 {{field}}…',
      add: '添加',
      cancel: '取消',
      addKey: '+ 添加字段',
    },
    importResult: {
      title: '导入结果',
      close: '关闭',
      imported: '已导入',
      errors: '错误',
      details: '错误详情：',
    },
  },
  candidates: {
    back: '← 返回黄金数据集',
    title: '黄金数据集候选用例',
    subtitle: '审核评测发现的问题，批准后加入黄金数据集。',
    awaiting: '{{n}} 个待审核',
    allReviewed: '已全部审核',
    refresh: '刷新',
    rejectSelected: '拒绝所选',
    approveSelected: '批准所选',
    header: '候选用例',
    status: { all: '全部', pending: '待审核', approved: '已批准', rejected: '已拒绝' },
    search: '按提示词、评估器或用例 key 搜索…',
    col: {
      prompt: '输入提示词',
      evaluator: '评估器',
      confidence: '置信度',
      suite: '用例集',
      date: '日期',
      status: '状态',
    },
    emptyPrompt: '（空）',
    loading: '正在加载候选用例…',
    noCandidates: '暂无候选用例',
    noCandidatesBody: '没有找到黄金数据集候选用例。',
    reviewTitle: '审核候选用例',
    reject: '拒绝',
    approve: '批准 → 加入黄金数据集',
    close: '关闭',
    evaluatorLabel: '评估器：',
    confidenceLabel: '置信度：',
    sessionLabel: '会话：',
    dateLabel: '日期：',
    rootCause: '根因',
    noRootCause: '（无根因）',
    evidence: '查看完整证据（S3）',
    fieldsTitle: '黄金数据集字段（批准前可编辑）',
    inputPrompt: '输入提示词 *',
    expectedBehavior: '预期行为',
    metrics: '评测指标（用逗号分隔）',
    approved: '已批准 → {{id}}（草稿）',
    approveFailed: '批准失败：{{error}}',
    unknown: '未知',
    rejected: '已拒绝候选用例',
    rejectFailed: '拒绝失败：{{error}}',
    bulkApproved: '已批准 {{n}} 个候选用例（草稿）',
    bulkApproveFailed: '批量批准失败：{{error}}',
    bulkRejected: '已拒绝 {{n}} 个候选用例',
    bulkRejectFailed: '批量拒绝失败：{{error}}',
  },
  evaluators: {
    title: '评估器',
    subtitle: '可用于在线和离线评测的评估器，选择一个类别开始浏览。',
    search: '搜索评估器…',
    all: '全部',
    builtin: '内置',
    custom: '自定义',
    noCategories: '没有匹配的类别',
    selectCategory: '请从左侧选择一个类别',
    categories: {
      'Response Quality': '回复质量',
      'Task Completion': '任务完成',
      'Tool Usage': '工具使用',
      Safety: '安全',
      Trajectory: '调用轨迹',
      Overall: '综合',
      'Non-LLM': '非 LLM',
      Core: '核心',
      RAG: 'RAG',
      'Content Quality': '内容质量',
      Agentic: 'Agent 能力',
      MCP: 'MCP',
      Conversational: '多轮对话',
      Multimodal: '多模态',
      Other: '其他',
      'Data Quality': '数据质量',
    },
    desc: {
      Builtin_Correctness: '评估 agent 回复中的信息是否符合事实。',
      Builtin_Faithfulness: '评估回复中的信息是否有所提供的上下文或来源支持。',
      Builtin_Helpfulness: '从用户角度评估 agent 回复的有用程度和价值。',
      Builtin_ResponseRelevance: '评估回复是否恰当地回应了用户的问题。',
      Builtin_Conciseness: '评估回复是否简洁，同时没有遗漏关键信息。',
      Builtin_Coherence: '评估回复是否结构清晰、逻辑连贯。',
      Builtin_InstructionFollowing: '衡量 agent 遵循系统指令的程度。',
      Builtin_Refusal: '检测 agent 回避问题或直接拒绝回答的情况。',
      Builtin_GoalSuccessRate: '评估对话是否成功达成用户目标。',
      Builtin_ToolSelectionAccuracy: '评估 agent 是否为任务选择了合适的工具。',
      Builtin_ToolParameterAccuracy: '评估 agent 从用户问题中提取参数的准确程度。',
      Builtin_Harmfulness: '评估回复是否包含有害内容。',
      Builtin_Stereotyping: '检测对个人或群体的刻板概括。',
      Builtin_TrajectoryExactOrderMatch: '严格顺序匹配：实际调用的工具与预期工具顺序完全一致，且没有多余调用。',
      Builtin_TrajectoryInOrderMatch: '按序匹配：预期工具按顺序出现在实际调用轨迹中，允许有多余调用。',
      Builtin_TrajectoryAnyOrderMatch: '任意顺序匹配：所有预期工具都被调用，顺序不限。',
      'Deval_OverallQuality-EfPz52HBTh': '评估整个会话的连贯性和回复质量，并检测严重问题（数值评分 v1）。',
      'Deval_Correctness-ruCQJ874wh': '参考 DeepEval 的正确性评估器：评估 agent 回复的事实准确性和忠实度。',
      'Deval_ResponseRelevance-cwiB6h5teD': '参考 DeepEval 的回复相关性评估器：评估回复是否回应了用户的问题。',
      'Deval_GoalSuccessRate-kolxG76EgT': '参考 DeepEval 的目标达成率评估器：评估用户目标是否达成。',
      'Deval_ToolSelectionAccuracy-zQIFg18O4v': '参考 DeepEval 的工具选择准确性评估器：评估是否选择了正确的工具。',
      'Deval_ToolParameterAccuracy-ZwmcLGDqLH': '参考 DeepEval 的工具参数准确性评估器：评估工具参数是否填写正确。',
      ExactMatchMetric: '检查输出是否与预期输出字符串完全一致。',
      PatternMatchMetric: '用正则或通配符模式校验输出。',
      GEval: '通用的 LLM-as-Judge 指标，可自定义评测标准。',
      ConversationalGEval: '适用于多轮对话评测的 GEval。',
      DAGMetric: '基于有向无环图的指标，用于评估结构化的任务流程。',
      AnswerRelevancyMetric: '评估回复是否与用户问题相关。',
      FaithfulnessMetric: '评估回复是否基于检索到的上下文。',
      ContextualRecallMetric: '衡量相关信息被检索到的比例。',
      ContextualRelevancyMetric: '评估检索到的上下文是否与问题相关。',
      ContextualPrecisionMetric: '衡量检索结果中相关上下文的精确度。',
      HallucinationMetric: '检测回复中捏造或缺乏依据的内容。',
      BiasMetric: '检测回复中带有偏见或歧视的内容。',
      ToxicityMetric: '评估回复是否包含恶意或冒犯性内容。',
      SummarizationMetric: '评估生成摘要的质量。',
      PIILeakageMetric: '检测回复中的个人身份信息。',
      NonAdviceMetric: '检测回复中不恰当的建议（金融、医疗、法律）。',
      MisuseMetric: '检测对 AI 系统的潜在滥用。',
      RoleViolationMetric: '检测 agent 超出其指定角色边界的情况。',
      RoleAdherenceMetric: '评估 agent 保持在既定角色内的程度。',
      TaskCompletionMetric: '评估 agent 是否完成了指定任务。',
      GoalAccuracyMetric: '衡量 agent 达成用户目标的准确程度。',
      ToolCorrectnessMetric: '评估工具选择和参数的准确性。',
      ToolUseMetric: '评估工具使用的整体质量和必要性。',
      StepEfficiencyMetric: '评估 agent 是否用了最少的必要步骤。',
      PlanAdherenceMetric: '评估 agent 执行时遵循既定计划的程度。',
      PlanQualityMetric: '评估 agent 行动计划的质量。',
      TopicAdherenceMetric: '评估 agent 是否保持在指定话题内。',
      MCPTaskCompletionMetric: '评估接入 MCP 的 agent 的任务完成情况。',
      MCPUseMetric: '评估 MCP 工具的使用质量。',
      ConversationCompletenessMetric: '评估对话中的所有话题是否都得到了充分回应。',
      TurnRelevancyMetric: '评估多轮对话中每一轮回复的相关性。',
      TurnFaithfulnessMetric: '评估对话中每一轮回复对上下文的忠实度。',
      TextToImageMetric: '评估文生图的质量。',
      ImageCoherenceMetric: '评估生成图片的连贯性和一致性。',
      ImageHelpfulnessMetric: '评估生成图片对用户请求的帮助程度。',
      PromptAlignmentMetric: '评估输出与提示词指令的一致程度。',
      JsonCorrectnessMetric: '校验 JSON 输出的结构和正确性。',
      KnowledgeRetentionMetric: '评估 agent 能否在多轮对话中保留上下文。',
      ArgumentCorrectnessMetric: '评估回复中论证的逻辑正确性。',
      helpfulness: '自定义 GEval 指标：评估 agent 是否给出实质性回答，而不只是表示收到。结合业务领域。',
      correctness: '自定义 GEval 指标：对照 expected_output 参考答案，评估 actual_output 的事实准确性。',
      data_accuracy: '自定义 GEval 指标：对照真实数据评估数值准确性，容差 1%，兼容不同的格式写法。',
      sql_correctness: '自定义 GEval 指标：对照参考 SQL 评估 agent 生成的 SQL，重点看表和过滤条件是否正确，并考虑 RLS。',
      completeness: '自定义 GEval 指标：评估回复是否覆盖了所有要点。',
      conciseness: '自定义 GEval 指标：评估回复是否简洁、没有多余的冗长内容。',
      coherence: '自定义 GEval 指标：评估逻辑连贯性和结构。',
    },
  },
  reports: {
    title: 'Eval - {{agent}} 报告',
    platformTitle: 'Eval - 报告',
    online: '在线评测',
    offline: '离线评测',
  },
  continuous: {
    threshold: '阈值 {{value}}',
    metricThreshold: '(≥ {{value}})',
    radarThresholdTip: '{{name}}：阈值 ≥ {{value}}',
    radarScoreTip: '{{name}}：{{score}}（阈值 ≥ {{value}}）',
    conversation: '对话',
    traceSummaryOne: '{{turns}} 轮 · {{tools}} 次工具调用 · {{messages}} 条消息',
    traceSummaryMany: '{{turns}} 轮 · {{tools}} 次工具调用 · {{messages}} 条消息',
    turn: '第 {{n}} 轮',
    user: '用户',
    toolIn: '输入',
    toolOut: '输出',
    noToolIo: '没有记录输入或输出',
    agentResponse: 'Agent 回复',
    histogramTitle: '会话与评测',
    sessionsLabel: '会话：',
    evaluationsLabel: '评测：',
    loadingChart: '正在加载图表…',
    noChartData: '该时间段内没有数据',
    sessionsBar: '{{date}}：{{n}} 个会话',
    evaluationsBar: '{{date}}：{{n}} 次评测',
    pageSizeOption: '{{n}} 条',
    backToDetail: '← 返回详情',
    reportFrame: '评测报告',
    backToList: '← 返回列表',
    loading: '加载中…',
    runDetail: '运行详情',
    loadingReportButton: '正在加载报告…',
    viewFullReport: '查看完整报告',
    runId: '运行 ID',
    status: '状态',
    score: '得分',
    passThreshold: '通过阈值',
    passRate: '通过率',
    sessions: '会话',
    evaluations: '评测',
    hasReport: '有报告',
    yes: '是',
    no: '否',
    timeWindow: '时间窗口',
    start: '开始',
    end: '结束',
    timestamps: '时间',
    createdLabel: '创建：',
    finishedLabel: '完成：',
    metrics: '评测指标',
    scoreOverview: '评估器得分概览',
    aggregatedScores: '评估器平均得分',
    perEvaluator: '各评估器汇总',
    colEvaluator: '评估器',
    colAvg: '平均',
    colCount: '次数',
    colPassRate: '通过率',
    reportTitle: '评测报告',
    loadingReport: '正在加载报告…',
    showFullReport: '展开完整报告',
    collapseReport: '收起报告',
    sessionsTitle: '会话（{{count}}）',
    searchSession: '搜索会话 ID…',
    allStatus: '全部状态',
    allMetrics: '全部指标',
    minScore: '最低分',
    maxScore: '最高分',
    clear: '清除',
    colSessionId: '会话 ID',
    colAvgScore: '平均分',
    colStatus: '状态',
    colLowestScore: '最低分',
    colLowestMetric: '最低分指标',
    colEvaluatedAt: '评测时间',
    loadingSessions: '正在加载会话…',
    loadingSessionDetail: '正在加载会话详情…',
    sessionScore: '会话得分：{{score}}',
    evaluatorScores: '评估器得分',
    noConversation: '该会话没有对话数据。',
    sessionsPage: '第 {{page}} / {{pages}} 页（{{n}} 个会话）',
    sessionsPageFiltered: '第 {{page}} / {{pages}} 页（{{n}} 个会话 / 共 {{total}} 个）',
    prev: '上一页',
    next: '下一页',
    evaluationsTitle: '评测（{{count}}）',
    loadingEvaluations: '正在加载评测…',
    evaluationsFailed: '加载评测失败。',
    aiReview: {
      title: '低分评测 AI 复核（{{count}}）',
      description: '从低于阈值的评测中抽样，由 LLM 结合对话逐条复核。只有严重程度为 critical 的会成为失败案例（首页 Error Log、洞察）。',
      loading: '正在加载 AI 复核…',
      failed: '加载 AI 复核失败。',
      empty: '本次运行没有被复核的低分评测。',
      verdicts: '判断：',
      severities: '严重程度：',
      falsePositiveShare: '误判（False Positive）：{{n}} / {{total}}（{{pct}}%）',
      colVerdict: 'AI 判断',
      colSeverity: '严重程度',
      colAction: '建议动作',
      colEvaluator: '评估器',
      colSession: '会话',
      colScore: '得分 / 阈值',
      colRootCause: '根因',
      colSuggestion: '建议',
    },
    allEvaluators: '全部评估器',
    allSessions: '全部会话',
    allLevels: '全部级别',
    levelTrace: 'Trace',
    levelSpan: 'Span',
    levelSession: 'Session',
    colSession: '会话',
    colLevel: '级别',
    colScore: '得分',
    colLabel: '标签',
    colAnalysis: '分析',
    explanation: '说明',
    noExplanation: '没有说明。',
    traceLabel: 'Trace：',
    spanLabel: 'Span：',
    dateLabel: '日期：{{date}}',
    aiAnalysis: 'AI 分析',
    rootCause: '根因：',
    suggestion: '建议：',
    evaluationsPage: '第 {{page}} / {{pages}} 页（{{n}} 次评测）',
    showing: '显示 {{n}} / {{total}} 次评测',
    colRunId: '运行 ID',
    colSessions: '会话',
    colRunPassRate: '通过率',
    colWindow: '时间窗口',
    colCreated: '创建时间',
    loadingRuns: '正在加载运行记录…',
    runsDescription: '对 agent 会话进行的评测运行。',
    refresh: '刷新',
    runsHeader: '评测运行',
    searchRuns: '按运行 ID 或状态搜索',
    filterRuns: '筛选运行记录',
    noRuns: '暂无评测运行',
    noRunsBody: '所选日期范围内没有评测运行。',
    preferences: '偏好设置',
    confirm: '确认',
    cancel: '取消',
    runsPerPage: '每页条数',
  },
  offline: {
    status: { SUCCESS: '已完成', FAILED: '出错', RUNNING: '运行中', INTERRUPTED: '已中断' },
    gate: {
      pending: '评测中',
      skipped: '已跳过',
      promoted: '已发布',
      notPromoted: '未发布',
      blocked: '已拦截',
    },
    type: { integration: '集成测试', promotion: '发布评测' },
    verdict: {
      integrationMeasured: '集成测试：已测得通过率',
      integrationRunning: '集成测试运行中',
      integrationRate: '通过率 {{rate}}%，是否通过由流水线审批流程决定',
      inProgress: '评测进行中',
      interrupted: '被部署中断',
      interruptedSub: '该变更已退回队列，将在下一个窗口重新运行',
      rate: '通过率 {{rate}}%，阈值 ≥ {{threshold}}%',
      rateUnavailable: '暂无通过率',
      notPromoted: '已通过，未发布',
      notPublishedReason: '测试已通过，但变更未发布：{{reason}}',
      notPublished: '测试已通过，但变更未发布',
      skipped: '没有测试用例：已跳过并发布',
      promoted: '通过门禁，变更已发布',
      failed: '运行未完成即失败',
      blocked: '未通过门禁，变更未发布',
      skippedSub: '没有与该变更匹配的测试用例，未经评测直接发布',
    },
    trigger: { approval: '审批流程', codeChange: '代码变更', scoped: '指定范围运行' },
    allCapabilities: '全部能力',
    changedCapabilities: '变更的能力',
    target: '目标',
    promotion: {
      title: '发布结果',
      colTarget: '目标',
      colStatus: '状态',
      colVersion: '版本',
      colNote: '备注',
      status: { promoted: '已发布', skipped: '已跳过', failed: '失败' },
    },
    runType: { all: '全部运行', normal: '仅发布评测', integration: '仅集成测试' },
    pageSizeOption: '{{n}} 条',
    backToList: '返回列表',
    loadingRun: '正在加载运行…',
    overview: {
      title: '运行概览',
      status: '状态',
      gate: '门禁',
      passRate: '通过率',
      threshold: '阈值',
      thresholdValue: '≥ {{n}}%',
      category: '类别',
      trigger: '触发方式',
      created: '创建时间',
      delivery: '交付方式',
      artifacts: '产物',
      openReport: '在新标签页打开报告',
    },
    testCases: '测试用例',
    fullReport: '完整 HTML 报告',
    loadingReport: '正在加载报告…',
    reportBody: '完整评测报告（包含所有用例）会在新标签页中打开。',
    openFullReport: '打开完整 HTML 报告',
    noStoredReport: '没有存档报告：该运行早于报告存入 S3 的时间，较新的运行会在这里提供完整 HTML 报告。',
    reportUnavailable: '该运行没有可用的报告。',
    col: {
      runId: '运行 ID',
      type: '类型',
      status: '状态',
      gate: '门禁',
      passRate: '通过率',
      category: '类别',
      trigger: '触发方式',
      created: '创建时间',
    },
    loadingRuns: '正在加载运行记录…',
    preferences: '偏好设置',
    confirm: '确认',
    cancel: '取消',
    runsPerPage: '每页条数',
    description: '每个代码变更在发布前都会评测，只有全部测试用例通过后才会发布到生产环境。',
    refresh: '刷新',
    header: '上线前评测运行',
    filterRunType: '按运行类型筛选',
    noRuns: '暂无上线前评测运行',
    noRunsBody: '该筛选条件下没有运行记录。',
  },
  cases: {
    unnamedTool: '（未命名工具）',
    llmCallsOne: '{{n}} 次 LLM 调用',
    llmCallsMany: '{{n}} 次 LLM 调用',
    tokens: '{{n}} 个 Token',
    noToolCalls: '本次运行没有工具调用。',
    toolCallsOne: '{{n}} 次工具调用',
    toolCallsMany: '{{n}} 次工具调用',
    toolIn: '输入',
    toolOut: '输出',
    loading: '正在加载测试用例…',
    noCases: '该运行没有单用例结果：它早于用例存档功能，或没有匹配到测试用例。请查看下方的完整报告。',
    search: '搜索用例 ID…',
    filterLabel: '按结果筛选',
    all: '全部',
    passed: '通过',
    failed: '失败',
    sortLabel: '用例排序',
    sortStatus: '排序：失败优先',
    sortHigh: '排序：得分从高到低',
    sortLow: '排序：得分从低到高',
    shown: '显示 {{n}} / {{total}}',
    noMatch: '没有符合筛选条件的用例。',
    overall: '综合 {{pct}}',
    input: '输入',
    answer: 'Agent 回答',
    metrics: '评测指标',
    noMetrics: '该用例没有记录评测指标。',
    trace: '调用轨迹',
    loadingTrace: '正在加载调用轨迹…',
    traceTooLarge: '调用轨迹太大，无法在这里显示，请打开下方的完整 HTML 报告。',
    noTrace: '该用例没有记录调用轨迹。',
  },
  retry: {
    decision: { RETRY: '将重试', SUPERSEDED: '已被取代', IN_FLIGHT: '进行中' },
    nothing: '没有可重试的目标：所有目标都已被更新的变更取代，或已在处理中。',
    queuedNow: '已加入重试队列，新的运行很快开始，并会出现在运行列表中。',
    queuedLater: '已加入重试队列，将在下一次整点扫描时开始，并会出现在运行列表中。',
    alreadyQueued: '该运行已在重试队列中。',
    failed: '重试失败：{{error}}',
    colTarget: '目标',
    colCategory: '类别',
    colDecision: '处理',
    colReason: '原因',
    retry: '重试',
    title: '重试这次运行？',
    cancel: '取消',
    queue: '加入重试队列',
    body: '自失败以来没有变化的目标会用当前的测试用例重新评测；有更新变更的目标会被跳过，由那次变更单独评测。',
  },
  askAgent: {
    button: '问 Eval Agent',
    trend: '分析 {{agent}} 最近的在线评测趋势：会话数和评测数怎么变化，有没有异常的日期？',
    runs: '对比 {{agent}} 最近的在线评测 run：得分和通过率有什么变化趋势，哪几次 run 最需要关注？',
    runSummary: '解读 {{agent}} 的在线评测 run {{runId}}：得分 {{score}}，通过率 {{passRate}}（通过阈值 {{threshold}}），共 {{sessions}} 个会话、{{evaluations}} 条评测。整体表现如何，主要问题在哪里？',
    scoreOverview: 'run {{runId}} 中哪些评估器得分最低？分别是什么原因造成的？',
    evaluator: 'run {{runId}} 中评估器 {{evaluator}} 的平均分是 {{avg}}，通过率 {{passRate}}。为什么是这个分数，失败的案例有什么共同点？',
    report: '总结 run {{runId}} 评测报告的关键发现和改进建议。',
    sessions: 'run {{runId}} 中得分最低的会话有哪些？它们分别是什么原因失败的？',
    session: '分析 run {{runId}} 中的会话 {{sessionId}}：平均分 {{score}}，得分最低的评估器是 {{metric}}（{{lowest}}）。这个会话哪里出了问题？',
    aiReview: '总结 run {{runId}} 的 AI 复核结果：哪些是真实问题、哪些是误判，应该优先修复什么？',
    aiReviewItem: 'AI 复核认为 run {{runId}} 中评估器 {{evaluator}} 对会话 {{sessionId}}（trace {{traceId}}）的判定是 {{verdict}}（严重度 {{severity}}，建议动作 {{action}}），得分 {{score}} / 阈值 {{threshold}}，根因：{{rootCause}}。请核实这个结论，并给出具体的修复方案。',
    evaluations: 'run {{runId}} 的评测明细中，哪些评估器失败最多？失败有什么规律？',
    evaluation: 'run {{runId}} 中，评估器 {{evaluator}} 对会话 {{sessionId}}（trace {{traceId}}）给出 {{score}} 分（{{label}}）。这个判定合理吗？',
    offline: {
      runs: '总结最近的离线（发布前）评测 run：哪些被 Gate 拦截了，通过率有什么趋势？',
      run: '解读离线评测 run {{runId}}（类型 {{type}}，状态 {{status}}，通过率 {{passRate}}）：结果如何，没通过的原因是什么？',
      verdict: '离线评测 run {{runId}} 的结论是「{{verdict}}」：{{detail}}。为什么是这个结论，下一步应该怎么做？',
      overview: '概括离线评测 run {{runId}}：评测目标 {{targets}}，触发方式 {{trigger}}，状态 {{status}}，通过率 {{passRate}}（阈值 {{threshold}}）。',
      promotion: 'run {{runId}} 中各个目标的发布结果如何？被跳过或失败的原因是什么？',
      promotionItem: 'run {{runId}} 中目标 {{target}} 的发布状态是 {{status}}（{{note}}）。为什么，应该怎么处理？',
      cases: 'run {{runId}} 中哪些测试用例失败了？失败有什么共同原因？',
      case: '分析离线评测 run {{runId}} 中的测试用例 {{caseId}}：结果 {{result}}，总分 {{score}}。哪里出了问题，应该如何改进？',
      metric: 'run {{runId}} 的测试用例 {{caseId}} 中，指标 {{metric}} 得分 {{score}}（阈值 {{threshold}}），评估理由：{{reason}}。这个判定合理吗，应该怎么改进？',
    },
    tasks: {
      overview: '分析 {{agent}} 在 {{start}} 至 {{end}} 期间的定时任务：{{active}} 个活跃任务，执行 {{executions}} 次，成功率 {{rate}}，失败 {{failed}} 次。有哪些需要关注的问题？',
      daily: '{{agent}} 在 {{start}} 至 {{end}} 期间每天的任务执行和失败有什么趋势？哪几天异常？',
      types: '{{agent}} 在 {{start}} 至 {{end}} 期间各类型任务的分布如何？哪类任务最容易失败？',
      durations: '{{agent}} 在 {{start}} 至 {{end}} 期间任务执行时长的分布如何？有没有耗时异常的任务？',
      failedList: '{{agent}} 在 {{start}} 至 {{end}} 期间失败最多的任务有哪些？失败原因是什么，怎么修复？',
      allList: '总结 {{agent}} 在 {{start}} 至 {{end}} 期间所有定时任务的运行情况：哪些任务需要关注？',
      failedItem: '任务「{{name}}」（owner {{owner}}）在 {{start}} 至 {{end}} 期间失败了 {{failures}} 次，最近一次错误：{{error}}。原因是什么，怎么修复？',
      item: '分析任务「{{name}}」（类型 {{type}}，owner {{owner}}）：最近状态 {{status}}，{{start}} 至 {{end}} 期间执行 {{executions}} 次、失败 {{failures}} 次。运行是否正常？',
    },
    usage: {
      overview: '分析 {{agent}} 在 {{start}} 至 {{end}} 期间的使用情况：{{users}} 个活跃用户、{{sessions}} 个会话、{{messages}} 条消息、{{toolCalls}} 次工具调用。有什么值得关注的地方？',
      daily: '{{agent}} 在 {{start}} 至 {{end}} 期间每日会话数有什么趋势？有没有异常的日期？',
      channels: '{{agent}} 在 {{start}} 至 {{end}} 期间各渠道的使用分布如何？',
      latency: '{{agent}} 在 {{start}} 至 {{end}} 期间的响应时间分布如何（平均 {{avg}}，P95 {{p95}}）？慢响应主要来自哪里？',
      tools: '{{agent}} 在 {{start}} 至 {{end}} 期间最常用的工具有哪些？调用分布是否合理？',
      tool: '{{agent}} 在 {{start}} 至 {{end}} 期间工具 {{tool}} 被调用了 {{count}} 次。它主要用在什么场景，调用有没有问题？',
      users: '{{agent}} 在 {{start}} 至 {{end}} 期间用户的使用分布如何？哪些用户最活跃？',
      user: '用户 {{user}} 在 {{start}} 至 {{end}} 期间使用 {{agent}}：{{sessions}} 个会话、{{tokens}} tokens，渠道 {{channel}}。这个用户主要用它做什么？',
    },
    insights: {
      intent: '{{agent}} 的洞察报告（{{date}}）中，用户意图「{{intent}}」排名第 {{rank}}，涉及 {{sessions}} 个会话。这类请求处理得怎么样，有什么改进空间？',
      categories: '总结 {{agent}} 洞察报告（{{date}}）中的优化方向：哪些问题影响最大，应该优先改进什么？',
      category: '{{agent}} 洞察报告（{{date}}）中的优化类别「{{category}}」影响了 {{sessions}} / {{total}} 个会话，共 {{rootCauses}} 个根因。请分析并给出改进优先级。',
      rootCause: '优化类别「{{category}}」下的根因「{{rootCause}}」，建议是：{{recommendation}}。请具体说明应该怎么落地。',
      failures: '总结 run {{runId}} 中的失败案例：主要有哪些问题，应该优先修复什么？',
      failure: '失败案例：run {{runId}} 中评估器 {{evaluator}} 对会话 {{sessionId}}（trace {{traceId}}）给出 {{score}} 分，判定 {{verdict}}（严重度 {{severity}}，建议动作 {{action}}），根因：{{rootCause}}。请核实并给出修复方案。',
    },
  },
};

export default evalNs;
